import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { useParams } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import getMessages from '../../lib/i18n/getMessages';
import { CurrencyProvider } from '../../hooks/useCurrency';
import ScheduleMeeting from '../../components/Succession/ScheduleMeeting/ScheduleMeeting';

const GET_SCHEDULE_MEETING_DATA = gql`
  query GetScheduleMeetingData($dealId: String!, $dealInterestId: ID!) {
    getDeal(name: $dealId) {
      id
      deal_name
      headline
      sub_headline
      closed
    }
    getDealInterest(id: $dealInterestId) {
      id
      workflow_state
      inventory_user {
        id
        user {
          id
          name
          allowed_name
          city
          province
        }
      }
    }
    company {
      id
      name
      deal_room_branded_name
      deal_room_branded_name_fr
      deal_room_single_word
      deal_room_single_word_fr
      stylesheet_name
      business_type
    }
    getCurrentUser {
      id
      name
      allowed_name
    }
  }
`;

const ScheduleMeetingContainer = (props, railsContext) => {
  window.locale = (railsContext && railsContext.i18nLocale) || window.locale || 'en';
  window.currency = (railsContext && railsContext.currency) || window.currency || 'USD';
  const { dealId, dealInterestId } = useParams();
  
  const { loading, error, data, refetch } = useQuery(GET_SCHEDULE_MEETING_DATA, {
    variables: { 
      dealId, 
      dealInterestId 
    },
    fetchPolicy: 'network-only'
  });
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (error) {
    console.error('Error loading schedule meeting data:', error);
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 my-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>We encountered an error while loading the meeting data. Please refresh the page or contact support.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  const deal = data?.getDeal;
  const dealInterest = data?.getDealInterest;
  const companySettings = data?.company || {};
  const currentUser = data?.getCurrentUser || {};
  
  if (!deal || !dealInterest) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Data not found</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>The requested deal or advisor information could not be found.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  const locale = window.locale;

  return (
    <CurrencyProvider currency={(railsContext && railsContext.currency) || window.currency || 'USD'}>
      <IntlProvider locale={locale} messages={getMessages(locale)}>
        <ScheduleMeeting
          deal={deal}
          dealInterest={dealInterest}
          companySettings={companySettings}
          currentUser={currentUser}
          refetchData={refetch}
        />
      </IntlProvider>
    </CurrencyProvider>
  );
};

export default ScheduleMeetingContainer; 